/** @format */
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { ModelSearchResultsType } from '@toucan/common/services/search/constants';
import { getModelFacetsQueryKey } from '@toucan/common/services/search/model/query-keys';
import { getModelSearchResultsFacets } from '@toucan/common/services/search/model/requests';
import {
    Facet,
    FacetValue,
    Query,
    SelectedFilters,
    SortBy,
} from '@toucan/common/services/search/types';

import { Milliseconds } from '@toucan/client/global/application';
import { toCamelCase } from '@toucan/client/global/axios';
import { Market } from '@toucan/client/global/market';

import { useShouldUseSemanticSearch } from '@toucan/client/features/search/components/model-search-results/hooks/use-should-use-semantic-search';

export function useFacetsQuery(
    baseQuery: Partial<Query>,
    disallowedFilters: string[],
    sort: SortBy,
    activeMarket: Market,
    selectedFilters: SelectedFilters,
    outOfStock: boolean,
    type: ModelSearchResultsType
) {
    const shouldUseSemanticSearch = useShouldUseSemanticSearch(type);

    const queryKey = useMemo(
        () => getModelFacetsQueryKey(type, baseQuery, selectedFilters),
        [type, baseQuery, selectedFilters]
    );

    // eslint-disable-next-line @tanstack/query/exhaustive-deps -- Legacy code: do not use as an example.
    const { data: facets, isLoading: isFacetsLoading } = useQuery({
        queryKey,
        queryFn: async () => {
            const facets = await getModelSearchResultsFacets(
                baseQuery,
                disallowedFilters,
                sort,
                activeMarket,
                selectedFilters,
                outOfStock,
                shouldUseSemanticSearch
            );

            const facetsWithCount: Facet[] = facets.map((facet) => {
                return {
                    ...facet,
                    count: facet.values.filter(
                        (value: FacetValue) => parseInt(value.count) > 0
                    ).length,
                };
            });

            return facetsWithCount;
        },
        select: (data) => {
            const allowedFacets = data?.filter(
                (facet: Facet) =>
                    !disallowedFilters.includes(toCamelCase(facet.field))
            );

            return allowedFacets;
        },
        staleTime: Milliseconds.FIVE_MINUTES,
    });

    if (isFacetsLoading) {
        return {
            facets: [],
            isLoading: true,
        };
    }

    return {
        facets,
        isLoading: false,
    };
}
